import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../../utils/mq';
import { useTranslation } from '../../../utils/translation';
import { useLinkComponent } from '../../../utils/component';
import { Cta } from '../../cta';
import { PriceData } from '../../types';
import ArrowPlainDown from '../../../icons/arrowPlainDown';

export type PricesCardProps = {
  prices: PriceData[];
  isCompact?: boolean;
  eventCta?: (shopName?: string, price?: string, position?: string, itemId?: number) => void;
  itemId?: number;
};

type CtaMobilePriceListProps = {
  price: PriceData;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 0 0 15px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 0 60px 45px;

      &.mod-compact {
        margin: 0 0 15px;
      }
    `,
  )}
`;

const PriceTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const PriceLine = styled.div`
  align-items: center;
  border-bottom: solid 1px var(--color-alt-3);
  display: flex;
  gap: 15px;
  padding: 10px 0;

  &:first-of-type {
    font-weight: 700;
  }
`;

const PriceLineLabel = styled.span`
  transition: color 0.3s;

  &:hover {
    color: var(--theme-color-primary);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 18px;
    `,
  )}
`;

const PriceLineValue = styled.span`
  display: none;
  font-size: 16px;
  margin-left: auto;
  transition: color 0.3s;

  &:hover {
    color: var(--theme-color-primary);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: block;
    `,
  )}
`;

const PriceLineCta = styled.div`
  a {
    white-space: nowrap;
  }
`;

const PriceLineCtaDesktop = styled(PriceLineCta)`
  display: none;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: block;
    `,
  )}
`;

const PriceLineCtaMobile = styled(PriceLineCta)`
  display: block;
  margin: 0 0 0 auto;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const PriceMore = styled.div`
  color: var(--text-color-alt);
  cursor: pointer;
  display: flex;
  font-family: var(--theme-typography-primary-font);
  font-size: 16px;
  font-weight: 700;
  gap: 10px;
  padding: 12px 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 18px;
    `,
  )}
`;

const Rotator = styled.div`
  tranform: initial;
  transition: all 0.3s;

  &.mod-rotated {
    transform: rotate(180deg);

    svg {
      position: relative;
      top: 6px;
    }
  }
`;

const RefurbishedPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-items: flex-start;
  align-items: flex-end;
  gap: 0px;
  cursor: pointer;
`;

const RefurbishedPrice = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;

const RefurbishedLabel = styled.div`
  font-size: 12px;
  line-height: 12px;
`;

const RefurbishedPriceElt = ({ price, refurbishedLabel }: { price: PriceData; refurbishedLabel: string }) => {
  return (
    <RefurbishedPriceContainer>
      <RefurbishedPrice>{price.price}</RefurbishedPrice>
      <RefurbishedLabel>{refurbishedLabel}</RefurbishedLabel>
    </RefurbishedPriceContainer>
  );
};

const RefurbishedCtaContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const RefurbishedLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 10px;
  margin-top: 4px;
  gap: 5px;
`;

const MobileRefurbishedLabel = styled.div`
  font-size: 10px;
  line-height: 10px;
`;

const ShopLogo = styled.img`
  height: 24px;
  width: 24px;
`;

const WaitingShopLogo = styled.div`
  height: 24px;
  width: 24px;
`;

const ShopName = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 16px;
    `,
  )}
`;

const CtaMobilePriceListCard = ({ price }: CtaMobilePriceListProps) => {
  const t = useTranslation();
  const ctaProps = {
    href: price.link,
    openInNewWindow: true,
  };

  if (price.condition && price.condition !== 'NEW') {
    return (
      <Cta className="mod-sm" rel={['nofollow']} {...ctaProps}>
        <RefurbishedCtaContainer>
          <RefurbishedLabelContainer>
            {price.price || t('design.miniCard.download')}
            <MobileRefurbishedLabel>{t('design.refurbished')}</MobileRefurbishedLabel>
          </RefurbishedLabelContainer>
        </RefurbishedCtaContainer>
      </Cta>
    );
  }

  return (
    <Cta className="mod-sm" rel={['nofollow']} {...ctaProps}>
      {price.price || t('design.miniCard.download')}
    </Cta>
  );
};

const PriceCard = ({ prices, isCompact = false, eventCta, itemId }: PricesCardProps) => {
  const [seeMorePrice, setSeeMorePrice] = useState(false);
  const [lazyShopLogo, setLazyShopLogo] = useState(false);

  useEffect(() => {
    if (!lazyShopLogo) {
      setLazyShopLogo(true);
    }
  }, [lazyShopLogo, setLazyShopLogo]);

  const getMorePrice = () => {
    setSeeMorePrice(!seeMorePrice);
  };

  const t = useTranslation();
  const LinkComponent = useLinkComponent();

  return (
    <Wrapper className={isCompact ? 'mod-compact' : ''}>
      <PriceTable>
        {prices?.map((price: PriceData, index: number) =>
          index < 5 || seeMorePrice === true ? (
            <PriceLine key={price.link}>
              <PriceLineLabel>
                <LinkComponent href={price.link} rel={['nofollow']} openInNewWindow>
                  <ShopName>
                    {price?.shop?.icon && lazyShopLogo && (
                      <ShopLogo src={price.shop.icon} alt={price?.shop?.name || ''} />
                    )}
                    {price?.shop?.icon && !lazyShopLogo && <WaitingShopLogo />}
                    {price?.shop?.name}
                  </ShopName>
                </LinkComponent>
              </PriceLineLabel>
              <PriceLineValue>
                {price.condition && price.condition !== 'NEW' ? (
                  <RefurbishedPriceElt price={price} refurbishedLabel={t('design.refurbished')} />
                ) : (
                  <LinkComponent href={price.link} rel={['nofollow']} openInNewWindow>
                    {price.price}
                  </LinkComponent>
                )}
              </PriceLineValue>
              <PriceLineCtaDesktop>
                <Cta
                  href={price.link}
                  rel={['nofollow']}
                  onClick={() => eventCta(price.price, price.shop.name, 'card-in-content', itemId)}
                  openInNewWindow
                >
                  {t('design.priceCard.seeOffer')}
                </Cta>
              </PriceLineCtaDesktop>
              <PriceLineCtaMobile>
                <CtaMobilePriceListCard price={price} />
              </PriceLineCtaMobile>
            </PriceLine>
          ) : null,
        )}
      </PriceTable>
      {prices?.length > 5 && (
        <PriceMore onClick={() => getMorePrice()}>
          <Rotator className={seeMorePrice ? 'mod-rotated' : null}>
            <ArrowPlainDown width={21} height={21} color="var(--text-color-alt)" />
          </Rotator>
          {seeMorePrice ? t('design.priceCard.getLessPrice') : t('design.priceCard.getMorePrice')}
        </PriceMore>
      )}
    </Wrapper>
  );
};

export default PriceCard;
